import { EmptyTips } from '@/components';
import { primaryColor } from '@/styles/variants';
import { getPresetDate } from '@/utils';
import { formatAmount } from '@/utils/format';
import useUrlParams from '@/utils/useUrlParams';
import { DatePicker, Spin } from '@arco-design/web-react';
import dayjs from 'dayjs';
import * as echarts from 'echarts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCategoryProportions, getTimeSeries } from './service';

const colorPalette = [
  primaryColor,
  '#BEEEBE',
  '#FFB6D9',
  '#AF92FF',
  '#A2DEFC',
  '#FF9D6F',
  '#D4F0D4',
  '#FFCCE6',
  '#C8B0FF',
  '#BCE6FD',
];

const CHART_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  height: 350,
  backgroundColor: '#fff',
  padding: 32,
  borderRadius: 4,
  position: 'relative',
  overflow: 'hidden', // 添加 overflow hidden
};

// 公共样式
const OVERLAY_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const LOADING_OVERLAY_STYLE: React.CSSProperties = {
  ...OVERLAY_STYLE,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 10,
};

const EMPTY_OVERLAY_STYLE: React.CSSProperties = {
  ...OVERLAY_STYLE,
  color: '#999',
};

/**
 * 图表覆盖层组件 - 用于显示 loading 和空数据状态
 */
interface ChartOverlayProps {
  loading: boolean;
  isEmpty: boolean;
}

const ChartOverlay: React.FC<ChartOverlayProps> = ({ loading, isEmpty }) => {
  if (loading) {
    return (
      <div style={LOADING_OVERLAY_STYLE}>
        <Spin loading={loading} />
      </div>
    );
  }

  if (isEmpty) {
    return <EmptyTips styles={EMPTY_OVERLAY_STYLE} />;
  }

  return null;
};

interface Result {
  metric: {
    category_name: string;
  };
  values: [string, string][]; // [日期, value]
}

interface TimeSeriesData {
  resultType: string;
  result: Result[];
}

interface PieData {
  cost: string;
  proportion: string;
  categoryName: string;
}

interface TransformedPieData {
  value: number;
  name: string;
  proportion: string;
}

/**
 * 将日期字符串转换为时间戳（秒级）
 */
const formatTimestamp = (dateStr: string): number => {
  if (!dateStr) return undefined;
  try {
    const date = new Date(dateStr);
    // 转换为秒级时间戳（除以1000）
    return Math.floor(date.getTime() / 1000);
  } catch (error) {
    console.error('日期格式错误:', dateStr, error);
    return 0;
  }
};

/**
 * 安全的图表 Hook - 专门针对 ECharts 4.x 和 React 18
 */
const useSafeEcharts = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const isInitializedRef = useRef(false);

  // 安全地初始化图表
  const initChart = useCallback(() => {
    if (!chartRef.current || isInitializedRef.current) {
      return chartInstanceRef.current;
    }

    // 清理现有实例
    if (chartInstanceRef.current) {
      try {
        chartInstanceRef.current.dispose();
      } catch (error) {
        // 忽略清理错误
      }
    }

    try {
      chartInstanceRef.current = echarts.init(chartRef.current);
      isInitializedRef.current = true;

      // 使用 ResizeObserver 替代 window resize 事件
      if ('ResizeObserver' in window) {
        resizeObserverRef.current = new ResizeObserver(() => {
          if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
            try {
              chartInstanceRef.current.resize();
            } catch (error) {
              console.warn('调整图表大小失败:', error);
            }
          }
        });

        resizeObserverRef.current.observe(chartRef.current);
      }

      return chartInstanceRef.current;
    } catch (error) {
      console.error('初始化图表失败:', error);
      return null;
    }
  }, []);

  // 安全地设置图表选项
  const setChartOption = useCallback((option: echarts.EChartsOption) => {
    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      return;
    }

    try {
      chartInstanceRef.current.setOption(option, true); // 使用 true 强制不合并
    } catch (error) {
      console.error('设置图表选项失败:', error);
    }
  }, []);

  // 安全地清理
  const disposeChart = useCallback(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    if (chartInstanceRef.current) {
      try {
        // 先清除所有事件
        chartInstanceRef.current.off('click');
        chartInstanceRef.current.off('legendselectchanged');
        chartInstanceRef.current.off('datazoom');

        // 延迟销毁，确保 React 已经完成 DOM 操作
        setTimeout(() => {
          if (chartInstanceRef.current && !chartInstanceRef.current.isDisposed()) {
            try {
              chartInstanceRef.current.dispose();
            } catch (error) {
              console.warn('销毁图表实例时出错:', error);
            }
          }
        }, 0);
      } catch (error) {
        console.warn('清理图表时出错:', error);
      } finally {
        chartInstanceRef.current = null;
        isInitializedRef.current = false;
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      disposeChart();
    };
  }, [disposeChart]);

  return {
    chartRef,
    initChart,
    setChartOption,
    disposeChart,
    getInstance: () => chartInstanceRef.current,
  };
};

/**
 * 消费类别占比堆叠面积图
 */
const ChartLine = ({ dateRange }: { dateRange: string[] }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<TimeSeriesData | null>(null);
  const [loading, setLoading] = useState(false);
  const { chartRef, initChart, setChartOption, disposeChart } = useSafeEcharts();
  const dataRef = useRef<TimeSeriesData | null>(null);

  const getData = async () => {
    try {
      setLoading(true);
      const { data } = await getTimeSeries({
        startTime: formatTimestamp(dateRange?.[0]),
        endTime: formatTimestamp(dateRange?.[1]),
      });
      dataRef.current = data || {};
      setData(data || {});
    } catch (error) {
      console.error('获取堆叠面积图数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理堆叠面积图数据
  const processChartData = (apiData: TimeSeriesData | null) => {
    if (!apiData || !apiData.result || apiData.result.length === 0) {
      return {
        xAxisData: [],
        seriesData: [],
        legendData: [],
        sortedDates: [],
      };
    }

    const allDates = new Set<string>();
    apiData.result.forEach((item) => {
      item.values?.forEach(([date]) => {
        allDates.add(date);
      });
    });

    const sortedDates = Array.from(allDates).sort();
    const xAxisData = sortedDates.map((date) => formatDate(date));

    const seriesData = apiData.result.map((item, index) => {
      const categoryName = item.metric.category_name;

      const valueMap = new Map<string, number>();
      item.values?.forEach(([date, value]) => {
        valueMap.set(date, parseFloat(value) || 0);
      });

      const seriesValues = sortedDates.map((date) => valueMap.get(date) || 0);

      return {
        name: categoryName,
        type: 'line' as const,
        stack: 'total',
        areaStyle: {
          color: colorPalette[index % colorPalette.length],
          opacity: 0.6,
        },
        emphasis: {
          focus: 'series',
          areaStyle: {
            opacity: 0.9,
          },
        },
        smooth: true,
        data: seriesValues,
        lineStyle: {
          width: 2,
        },
        symbol: 'circle',
        symbolSize: 6,
      };
    });

    const legendData = apiData.result.map((item) => item.metric.category_name);

    return { xAxisData, seriesData, legendData, sortedDates };
  };

  // 格式化日期显示
  const formatDate = (dateStr: string) => {
    try {
      if (dateStr.includes('-')) {
        const date = new Date(dateStr);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  useEffect(() => {
    getData();
  }, [dateRange]);

  useEffect(() => {
    if (!data) return;

    // 延迟初始化图表，确保 DOM 已经准备好
    const timer = setTimeout(() => {
      const chartInstance = initChart();
      if (!chartInstance) return;

      const { xAxisData, seriesData, legendData, sortedDates } = processChartData(data);

      const option: echarts.EChartsOption = {
        color: colorPalette,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985',
            },
          },
          formatter: (params: any) => {
            if (!Array.isArray(params) || params.length === 0) return '';
            const dateIndex = params[0].dataIndex;
            const originalDate = sortedDates[dateIndex] || '';

            const total = params.reduce((sum: number, param: any) => sum + (param.value || 0), 0);

            let tooltip = `<div style="font-weight: bold; margin-bottom: 8px;">${originalDate}</div>`;

            const sortedParams = [...params].sort(
              (a: any, b: any) => (b.value || 0) - (a.value || 0),
            );

            sortedParams.forEach((param: any) => {
              const percentage = total > 0 ? ((param.value / total) * 100).toFixed(1) : '0';
              tooltip += `
                <div style="display: flex; justify-content: space-between; align-items: center; margin: 4px 0;">
                  <span>
                    <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${param.color}; margin-right: 6px;"></span>
                    ${param.seriesName}
                  </span>
                  <span style="font-weight: 500; margin-left: 16px;">
                    ${formatAmount({ amount: param.value })} (${percentage}%)
                  </span>
                </div>
              `;
            });

            tooltip += `
              <div style="border-top: 1px solid #eee; margin-top: 8px; padding-top: 8px; display: flex; justify-content: space-between; font-weight: bold;">
                <span>${t('tenant:total')}</span>
                <span>${formatAmount({ amount: total })}</span>
              </div>
            `;

            return tooltip;
          },
        },
        legend: {
          data: legendData,
          type: legendData.length > 6 ? 'scroll' : 'plain',
          bottom: 0,
          icon: 'circle',
          itemWidth: 8,
          itemHeight: 8,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: legendData.length > 5 ? '15%' : '10%',
          top: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLabel: {
            rotate: xAxisData.length > 10 ? 45 : 0,
            interval: 'auto',
          },
          axisLine: {
            lineStyle: {
              color: '#ccc',
            },
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: number) => formatAmount({ amount: value }),
          },
          axisLine: {
            lineStyle: {
              color: '#ccc',
            },
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#f0f0f0',
            },
          },
        },
        series: seriesData,
        // dataZoom:
        //   xAxisData.length > 10
        //     ? [
        //         {
        //           type: 'inside',
        //           start: 0,
        //           end: 100,
        //         },
        //         {
        //           type: 'slider',
        //           start: 0,
        //           end: 100,
        //           bottom: legendData.length > 5 ? '5%' : 0,
        //           height: 20,
        //         },
        //       ]
        //     : undefined,
        animation: false, // 关闭动画减少冲突
      };

      setChartOption(option);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [data, initChart, setChartOption, t]);

  // 在日期范围变化时重新初始化图表
  useEffect(() => {
    return () => {
      // 在组件卸载或日期范围变化时清理图表
      disposeChart();
    };
  }, [dateRange, disposeChart]);

  const isEmpty = !data?.result || data.result.length === 0;

  if (isEmpty || loading)
    return (
      <div style={CHART_CONTAINER_STYLE}>
        <ChartOverlay loading={loading} isEmpty={isEmpty} />
      </div>
    );

  return <div ref={chartRef} style={CHART_CONTAINER_STYLE}></div>;
};

/**
 * 转换饼图数据格式
 */
const transformPieData = (pieData: PieData[]): TransformedPieData[] => {
  return pieData.map((item) => ({
    value: parseFloat(item.cost) || 0,
    name: item.categoryName,
    proportion: item.proportion,
  }));
};

/**
 * 消费类别占比饼图
 */
const ChartPie = ({ dateRange }: { dateRange: string[] }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<PieData[]>([]);
  const [loading, setLoading] = useState(false);
  const { chartRef, initChart, setChartOption, disposeChart } = useSafeEcharts();

  const getData = async () => {
    try {
      setLoading(true);
      const res = await getCategoryProportions({
        startTime: formatTimestamp(dateRange?.[0]),
        endTime: formatTimestamp(dateRange?.[1]),
      });
      setData(res || []);
    } catch (error) {
      console.error('获取饼图数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [dateRange]);

  useEffect(() => {
    if (data?.length === 0) return;

    const timer = setTimeout(() => {
      const chartInstance = initChart();
      if (!chartInstance) return;

      const option: echarts.EChartsOption = {
        color: colorPalette,
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            const { name, value, percent, data } = params;
            return `
              ${name}<br/>
              ${t('data-center:total.amount')}: ${formatAmount({ amount: value })}<br/>
              ${t('system:proportion')}: ${data.proportion || percent}%
            `;
          },
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          formatter: (name: string) => {
            const item = data.find((d) => d.categoryName === name);
            return item ? `${name} (${item.proportion}%)` : name;
          },
        },
        series: [
          {
            name: t('common:consumption.category'),
            type: 'pie',
            radius: '80%',
            center: ['50%', '50%'],
            data: transformPieData(data),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
            label: {
              formatter: '{b}: {d}%',
              show: false,
            },
          },
        ],
      };

      setChartOption(option);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [data, initChart, setChartOption, t]);

  useEffect(() => {
    return () => {
      disposeChart();
    };
  }, [dateRange, disposeChart]);

  return (
    <div ref={chartRef} style={CHART_CONTAINER_STYLE}>
      <ChartOverlay loading={loading} isEmpty={!loading && data?.length === 0} />
    </div>
  );
};

/**
 * 图表组件容器 - 使用 key 强制重新渲染
 */
function Charts() {
  const { t } = useTranslation();
  const { getParam, setParam } = useUrlParams();
  const [dateRange, setDateRange] = useState<string[]>([]);
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    const range = getParam('dateRange');
    if (range) {
      setDateRange(JSON.parse(range));
    } else {
      // 默认7天，起始时间00:00:00，结束时间23:59:59
      const end = dayjs().endOf('day');
      const start = end.subtract(7, 'day').startOf('day');
      onChange([start.format('YYYY-MM-DD HH:mm:ss'), end.format('YYYY-MM-DD HH:mm:ss')]);
    }
  }, []);

  // 处理日期范围变化，确保包含时间
  const onChange = (dates: string[]) => {
    let formattedDates = dates;

    if (dates.length === 2) {
      // 为起始日期添加 00:00:00，为结束日期添加 23:59:59
      formattedDates = [
        dayjs(dates[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        dayjs(dates[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      ];
    }

    setParam('dateRange', JSON.stringify(formattedDates));
    setDateRange(formattedDates);
    // 强制重新渲染图表组件
    setChartKey((prev) => prev + 1);
  };

  // 获取仅日期部分用于显示（去掉时间部分）
  const getDisplayDateRange = () => {
    if (dateRange.length === 2) {
      return [dayjs(dateRange[0]).format('YYYY-MM-DD'), dayjs(dateRange[1]).format('YYYY-MM-DD')];
    }
    return dateRange;
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <h3>{t('tenant:consumption.overview')}</h3>
        <DatePicker.RangePicker
          style={{ minWidth: 450, maxWidth: 500, width: '21vw', marginBottom: 10 }}
          position="br"
          allowClear={false}
          value={getDisplayDateRange()}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              onChange([dates[0], dates[1]]);
            } else {
              onChange([]);
            }
          }}
          disabledDate={(current) => current.isAfter(dayjs())}
          shortcutsPlacementLeft
          shortcuts={getPresetDate()}
        />
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <ChartLine key={`line-${chartKey}`} dateRange={dateRange} />
        <ChartPie key={`pie-${chartKey}`} dateRange={dateRange} />
      </div>
    </>
  );
}

export default Charts;
